import { CommonModule } from '@angular/common';
import { Component, computed, TemplateRef, viewChild } from '@angular/core';
import { CustomWidget } from '../widget';
import { AbbDateComponent } from '@components/abb-date/abb-date.component';
import { NumberPipe } from '@pipes/number.pipe';
import { TPipe } from '@pipes/t.pipe';
import { WidgetType } from '@constants/widgets';
import { OverflowTooltipDirective } from '@directives/overflow-tooltip.directive';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AbbStatusIconComponent } from '@components/abb-status-icon/abb-status-icon.component';

export type TableWidgetCol<T extends Record<string, any>> = {
  name: string;
  colClass?: string;
  rowClass?: string;
  renderer: (
    v: T
  ) =>
    | { value: string; templateRef?: undefined; ctx?: undefined }
    | { templateRef: TemplateRef<any>; value?: undefined; ctx?: any };
  skipTranslation: boolean;
};

@Component({
  selector: 'abb-table-widget',
  standalone: true,
  imports: [
    CommonModule,
    AbbDateComponent,
    NumberPipe,
    TPipe,
    MatProgressSpinnerModule,
    AbbStatusIconComponent,
  ],
  template: `
    <div class="w-full overflow-x-auto">
      @if(loadingWidgetData()) {
      <mat-spinner class="mx-auto" [diameter]="50"></mat-spinner>
      } @else if (propertyName()) {
      <span class="text-abb-font/60 mb-1">
        {{ propertyName() }}
      </span>
      } @else if (groupName()) {
      <span class="text-abb-font/60 mb-1">{{ groupName() }}</span>
      }@else if (deviceName()) {
      <span class="text-abb-font/60 mb-1">{{ deviceName() }}</span>
      }
      <table class="min-w-max table-auto border-collapse">
        <thead>
          <tr>
            @for (col of columns(); track $index) {
            <th
              overflowTooltip
              class="px-2 py-2 text-left align-top whitespace-pre-line break-words min-w-0"
              [ngClass]="col.colClass ?? ''"
              [innerHTML]="transformToUpperCase(col.name | t)"
            >
            </th>
            }
          </tr>
        </thead>
        <tbody>
          @for (row of rows(); track $index) {
          <tr class="border border-abb-black/10 odd:bg-abb-black/[0.02]">
            @for (col of columns(); track $index) {
            <td
              class="text-abb-font/80 py-1 px-2 align-top whitespace-nowrap"
              [ngClass]="col.rowClass ?? ''"
            >
              @if (col.renderer(row); as value) { @if (value.templateRef) {
              <ng-container
                *ngTemplateOutlet="
                  value.templateRef;
                  context: { $implicit: value.ctx }
                "
              ></ng-container>
              } @else {
              {{ value.value | t }}
              } }
            </td>
            }
          </tr>
          }
        </tbody>
      </table>
    </div>

    <ng-template #dateTemplate let-item>
      <abb-date [date]="item" [showTime]="true"></abb-date>
    </ng-template>
    <ng-template #numberTemplate let-item>
      <span>{{
        item
          | number
            : {
                maximumFractionDigits: maximumFractionDigits(),
                maximumSignificantDigits: maximumSignificantDigits()
              }
      }}</span>
    </ng-template>
    <ng-template #stringTemplate let-item>
      <span>{{ item | t }}</span>
    </ng-template>
    <ng-template #statusTemplate let-item>
      <div class="flex items-center gap-x-3">
        @if (item === 'OK' || item === 'WARNING') {
        <abb-status-icon
          variant="outlined"
          size="small"
          [status]="item"
        ></abb-status-icon>
        } @else {
        <div
          class="h-4 w-4 rounded-full"
          [ngClass]="{
            'bg-abb-success': item === 'on' || item === 'On',
            'bg-abb-error': item === 'off' || item === 'Off'
          }"
        ></div>
        }
        <span>
          {{
            item === 'on' || item === 'On'
              ? ('On' | t)
              : item === 'off' || item === 'Off'
              ? ('Off' | t)
              : item === 'OK'
              ? ('Okay' | t)
              : item === 'WARNING'
              ? ('Offline' | t)
              : item
          }}
        </span>
      </div>
    </ng-template>
  `,
  styles: ``,
  host: {
    class: 'w-full h-full block overflow-auto',
  },
})
export class TableWidgetComponent extends CustomWidget {
  dateTemplate = viewChild<TemplateRef<any>>('dateTemplate');
  numberTemplate = viewChild<TemplateRef<any>>('numberTemplate');
  stringTemplate = viewChild<TemplateRef<any>>('stringTemplate');
  statusTemplate = viewChild<TemplateRef<any>>('statusTemplate');

  maximumFractionDigits = computed(() =>
    this.widget().type === WidgetType.ACTUAL_VS_TARGET_VALUES ? 9 : 2
  );
  maximumSignificantDigits = computed(() =>
    this.widget().type === WidgetType.ACTUAL_VS_TARGET_VALUES ? 10 : undefined
  );
  propertyName = computed(() => {
    return this.widget().propertyId
      ? this.properties().find((p) => p.id === this.widget().propertyId)?.name
      : '';
  });
  groupName = computed(() => {
    return this.widget().groupId
      ? this.groups().find((g) => g.id === this.widget().groupId)?.name
      : '';
  });
  deviceName = computed(() => {
    return this.widget().deviceId
      ? this.deviceList().find((d) => d.id === this.widget().deviceId)?.name
      : '';
  });
  rows = computed(() => {
    const data = this.data();
    if (data && data.__typename === 'TableData') {
      return data.items;
    }
    return [];
  });

  columns = computed<TableWidgetCol<Array<string>>[]>(() => {
    const dateTemplate = this.dateTemplate();
    const numberTemplate = this.numberTemplate();
    const stringTemplate = this.stringTemplate();
    const statusTemplate = this.statusTemplate();
    const data = this.data();
    if (
      !dateTemplate ||
      !numberTemplate ||
      !stringTemplate ||
      !statusTemplate ||
      !data ||
      data.__typename !== 'TableData'
    )
      return [];

    return data.columns.map((c, i) => ({
      name: c.label,
      renderer: (v) => ({
        ctx:
          c.type === 'date'
            ? new Date(Number(v[i]))
            : c.type === 'dateAndTime'
            ? new Date(v[i])
            : v[i],
        templateRef:
          c.type === 'date' || c.type === 'dateAndTime' || c.type === 'time'
            ? dateTemplate
            : c.type === 'number'
            ? numberTemplate
            : c.type === 'status'
            ? statusTemplate
            : stringTemplate,
      }),
      rowClass: 'text-left',
      colClass: 'text-left',
      skipTranslation: false,
    }));
  });

  transformToUpperCase(name: string): string {
    const upperName = name.toUpperCase();
    const regex = /(\[.*?\])/;
    const match = regex.exec(upperName);

    if (match) {
      const unit = match[0];
      const beforeUnit = upperName.split(unit)[0].trim();
      const words = beforeUnit.split(' ');

      // stringhe con unità di misura
      if (words.length >= 2 && beforeUnit.length >= 10) {
        // l'ultima parola con l'unità di misura va mantenuta nella seconda riga
        const lastWord = words[words.length - 1];
        const firstPart = words.slice(0, -1).join(' ');
        const secondPart = `${lastWord} ${unit}`;
        return `${firstPart}\n${secondPart}`;
      } else {
        return `${beforeUnit} ${unit}`;
      }
    }

    // Per stringhe senza unità
    const words = upperName.split(' ');
    if (words.length >= 2 && upperName.length >= 10) {
      const midPoint = Math.ceil(words.length / 2);
      const firstLine = words.slice(0, midPoint).join(' ');
      const secondLine = words.slice(midPoint).join(' ');
      return `${firstLine}\n${secondLine}`;
    }

    return upperName;
  }
}
